import { isEmpty, path } from 'rambda';
import { type ChangeEvent, useEffect, useRef } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOnClickOutside } from 'usehooks-ts';
import ConversationAttachmentMenu from '@/components/conversations/attachment-form/conversation-attachment-menu';
import useConversationForm from '@/hooks/conversations/use-conversation-form';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import useFileSizeLimit from '@/hooks/files/use-file-size-limit';
import { convertFileToUploadable, type Uploadable } from '@/models/uploadable';
import { addToast } from '@/stores/toast';
import { readableFileSize } from '@/utils/file-size';
import { FILE_LIMIT_MAP, FILE_TYPE_MESSAGE_MAP, FileType } from '@/utils/file-type';
import FileSelectorFlyout from './file-selector-flyout';

interface Props {
  open: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  onClose?: () => void;
}

export function ConversationAttachmentForm({ open, disabled, onClose }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const {
    form: { control, reset, formState, clearErrors, getValues },
    submit,
  } = useConversationForm();

  const { isAdminSession } = useConversationStore();
  const { getFileSizeLimit } = useFileSizeLimit(isAdminSession);
  const { field: filesField } = useController({ control, name: 'Attachment' });
  const { field: fileTypeField } = useController({ control, name: 'AttachmentType' });
  const [files, fileType] = useWatch({ control, name: ['Attachment', 'AttachmentType'] });

  const fileLimit = fileType ? path([fileType], FILE_LIMIT_MAP) : 0;

  const attachmentFormRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(attachmentFormRef, () => {
    if (document.getElementById('chatroom-modal') || !open) return;
    onClose?.();
  });

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    reset();
  };

  const summonFileSelectDialog = (fileType: FileType, capture?: string) => {
    if (!fileInputRef.current) return;

    // reset file input
    fileInputRef.current.removeAttribute('multiple');
    fileInputRef.current.removeAttribute('maxlength');
    fileInputRef.current.removeAttribute('accept');
    fileInputRef.current.removeAttribute('capture');

    fileTypeField.onChange(fileType);
    fileInputRef.current.setAttribute('accept', fileType);
    if (capture) {
      fileInputRef.current.setAttribute('capture', capture);
    } else {
      fileInputRef.current.removeAttribute('capture');
    }

    const hasQuote = getValues('Quote') !== undefined;
    const limit = hasQuote ? 1 : FILE_LIMIT_MAP[fileType];
    if (limit > 1) {
      fileInputRef.current.setAttribute('multiple', '');
      fileInputRef.current.setAttribute('maxlength', limit.toString());
    } else {
      fileInputRef.current.removeAttribute('multiple');
      fileInputRef.current.removeAttribute('maxlength');
    }
    fileInputRef.current.click();
  };

  const handleSelectFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;

    if (!selectedFiles?.length) return;
    const uploadables: Uploadable[] = [];
    for (const file of selectedFiles) {
      const fileSizeLimit = getFileSizeLimit(file.type.split('/')[0]);
      if (file.size > fileSizeLimit) {
        addToast({
          type: 'error',
          message: t('errors.file-size-limit', { limit: readableFileSize(fileSizeLimit) }),
        });
        if (fileType !== FileType.File || fileType === undefined) {
          uploadables.push(convertFileToUploadable(file));
        }
      } else {
        uploadables.push(convertFileToUploadable(file));
      }
    }

    if (!isEmpty(uploadables)) {
      filesField.onChange(uploadables.concat(files ?? []));
      e.target.value = '';

      if (fileType === FileType.File || fileType === undefined) {
        submit();
        clearFileInput();
      }
    }
  };

  useEffect(() => {
    if (formState.errors.Attachment && fileType) {
      addToast({
        type: 'error',
        message: t('errors.multi-file-select-limit', {
          unit: t(FILE_TYPE_MESSAGE_MAP[fileType], { count: fileLimit }),
        }),
      });
      clearErrors('Attachment');
    }
  }, [formState.errors.Attachment, fileLimit, fileType]);

  return (
    <>
      <ConversationAttachmentMenu
        ref={attachmentFormRef}
        disabled={disabled}
        isOpen={open}
        onPick={summonFileSelectDialog}
      />

      {fileType && fileType !== FileType.File && (
        <FileSelectorFlyout
          files={files ?? []}
          fileLimit={fileLimit}
          onCancel={clearFileInput}
        />
      )}

      <input
        {...filesField}
        value={undefined}
        onChange={handleSelectFiles}
        type='file'
        ref={(r) => {
          filesField.ref(r);
          fileInputRef.current = r;
        }}
        className='hidden'
      />
    </>
  );
}
